import { ChevronRight } from '@mui/icons-material'
import { Box, BoxProps, ButtonBase, Divider, Typography, styled } from '@mui/material'
import React, { PropsWithChildren } from 'react'
import { colors } from '../../constants/colors'
import { HBox, VBox } from '../../elements/basic-elements'

export const SETTINGS_GROUP_RADIUS = 20

/**
 * `grey` sits on white / card surfaces (e.g. compact modals).
 * `white` sits on grey surfaces (e.g. grey full-screen modals, page backgrounds).
 */
export type SettingsGroupBackground = 'grey' | 'white'

type SettingsSectionProps = PropsWithChildren<
  {
    title?: string
    /** Helper text under the title, e.g. why the section is disabled */
    description?: string
    background?: SettingsGroupBackground
  } & Omit<BoxProps, 'title'>
>

/** A titled group of settings rows: "How I'm notified", "Interactions", etc. */
export const SettingsSection = ({
  title,
  description,
  background,
  children,
  ...boxProps
}: SettingsSectionProps) => (
  <VBox gap={1.5} {...boxProps}>
    {(title || description) && (
      <Box>
        {title && <SettingsSectionTitle>{title}</SettingsSectionTitle>}
        {description && <SettingsDescription>{description}</SettingsDescription>}
      </Box>
    )}
    <SettingsGroup background={background}>{children}</SettingsGroup>
  </VBox>
)

export const SettingsSectionTitle = ({ children }: PropsWithChildren) => (
  <Typography variant="body1" fontWeight={500} color="textPrimary">
    {children}
  </Typography>
)

/**
 * Rounded card that stacks its children as rows, inserting a divider between
 * each one. Falsy children (conditional rows) are skipped.
 */
export const SettingsGroup = ({
  background = 'grey',
  children
}: PropsWithChildren<{ background?: SettingsGroupBackground }>) => {
  const rows = React.Children.toArray(children).filter(Boolean)
  return (
    <GroupCard background={background}>
      {rows.map((row, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Divider />}
          <Box py={1.25} px={2.5}>
            {row}
          </Box>
        </React.Fragment>
      ))}
    </GroupCard>
  )
}

const GroupCard = styled(VBox, {
  shouldForwardProp: prop => prop !== 'background'
})<{ background: SettingsGroupBackground }>`
  ${({ theme, background }) => {
    const isDark = theme.palette.mode === 'dark'
    const grey = isDark ? colors.DARK_MODE_80 : colors.BLACK_10
    const white = isDark ? colors.DARK_MODE_70 : colors.WHITE
    return `
      background: ${background === 'grey' ? grey : white};
      border-radius: ${SETTINGS_GROUP_RADIUS}px;
      padding: ${theme.spacing(0.5, 0)};
    `
  }}
`

/** A tappable settings row with a label and a trailing chevron. */
export const SettingsLinkRow = ({
  label,
  description,
  icon,
  onClick,
  hideChevron,
  color = 'textPrimary'
}: {
  label: React.ReactNode
  description?: string
  icon?: React.ReactNode
  onClick: () => void
  hideChevron?: boolean
  color?: string
}) => (
  <ButtonBase
    onClick={onClick}
    disableTouchRipple
    sx={{ width: '100%', textAlign: 'left', justifyContent: 'stretch', borderRadius: 1 }}
  >
    <HBox gap={2} alignItems="center" justifyContent="space-between" width="100%">
      <HBox gap={1} alignItems="center">
        {icon}
        <Box>
          <Typography variant="body1" fontWeight={500} color={color}>
            {label}
          </Typography>
          {description && <SettingsDescription>{description}</SettingsDescription>}
        </Box>
      </HBox>
      {!hideChevron && <ChevronRight sx={{ color: 'text.primary' }} />}
    </HBox>
  </ButtonBase>
)

export const SettingsDescription = styled(Typography)`
  ${({ theme }) => `
    font-size: 12px;
    line-height: 1.4;
    margin-top: 2px;
    color: ${theme.palette.mode == 'dark' ? colors.DARK_MODE_30 : colors.BLACK_50};
  `}
`
